import React, { memo, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useAppSelector } from "./store/index.ts";

const DynamicHead = memo(
  ({ content }) => {
    useEffect(() => {
      if (!content) return;

      // важно что бы вызывался только один раз
      if (window.DynamicHead?.content == content) return;

      window.DynamicHead = {};
      window.DynamicHead.content = content;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      // Удаляем meta и link теги они вставляются в другом месте
      const metaLinks = Array.from(tempDiv.querySelectorAll("meta, link"));
      metaLinks.forEach((el) => el.parentNode.removeChild(el));

      // Handle script elements separately
      const scripts = Array.from(tempDiv.querySelectorAll("script"));
      scripts.forEach((script) => {
        const newScript = document.createElement("script");
        if (script.src) {
          newScript.src = script.src;
        } else {
          newScript.textContent = script.innerHTML;
        }
        document.head.appendChild(newScript);
      });

      // Remove script elements from tempDiv
      scripts.forEach((script) => script.parentNode.removeChild(script));

      // Append the remaining sanitized content
      document.head.innerHTML += tempDiv.innerHTML;

      // Cleanup
      return () => {
        scripts.forEach((script) => {
          if (script.src) {
            const existingScript = document.querySelector(`script[src="${script.src}"]`);
            if (existingScript) document.head.removeChild(existingScript);
          } else {
            const existingScripts = Array.from(document.querySelectorAll("script")).filter(
              (s) => s.textContent === script.innerHTML
            );
            existingScripts.forEach((s) => document.head.removeChild(s));
          }
        });
        // Remove other appended elements
        Array.from(tempDiv.children).forEach((child) => {
          if (document.head.contains(child)) {
            document.head.removeChild(child);
          }
        });
      };
    }, [content]);

    DynamicHead.displayName = "DynamicHead";
    return null;
  },
  (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
  }
);

const DynamicBody = ({ content }) => {
  useEffect(() => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    const appendedElements = [];

    // элементы скрипта обрабатываются отдельно.
    const scripts = Array.from(tempDiv.querySelectorAll("script"));
    scripts.forEach((script) => {
      const newScript = document.createElement("script");
      if (script.src) {
        newScript.src = script.src;
      } else {
        newScript.textContent = script.innerHTML;
      }
      document.body.appendChild(newScript);
      appendedElements.push(newScript);
    });

    // Удаляем элементы скрипта из tempDiv.
    scripts.forEach((script) => script.parentNode.removeChild(script));

    // Добавляем оставшееся очищенное содержимое перед </body>
    while (tempDiv.firstChild) {
      const node = tempDiv.firstChild;
      document.body.insertBefore(node, document.body.lastChild);
      appendedElements.push(node);
    }

    // Очистка
    return () => {
      appendedElements.forEach((element) => {
        if (document.body.contains(element)) {
          document.body.removeChild(element);
        }
      });
    };
  }, [content]);

  return null;
};

export default function Head({ favicon, appleTouchIcon }) {
  const lang = useAppSelector((state) => state.common.lang);
  const title = useAppSelector((state) => state.common.title);
  const htmlHead = useAppSelector((state) => state.common.htmlHead);
  const htmlEndBody = useAppSelector((state) => state.common.htmlEndBody);
  const isTgBot = useAppSelector((state) => state.common.isTgBot);

  useEffect(() => {
    if (isTgBot) {
      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-web-app.js";
      script.async = true;
      script.onload = () => {
        if (window.Telegram && window.Telegram.WebApp) {
          const WebApp = window.Telegram.WebApp;
          WebApp.expand();
          WebApp.enableClosingConfirmation();
          console.log("window.Telegram?.WebApp", WebApp);
        }
      };
      script.onerror = () => {
        console.error("Failed to load the Telegram Web App script");
      };
      document.head.appendChild(script);
      return;
    }
  }, [isTgBot]);

  const parseHtmlHead = (htmlString) => {
    if (!htmlString) return { tags: [], scripts: [] };

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const tags = [];
    const scripts = [];

    // Собираем все meta и link теги
    doc.querySelectorAll("meta, link").forEach((el) => {
      const tag = {};
      Array.from(el.attributes).forEach((attr) => {
        tag[attr.name] = attr.value;
      });
      tags.push({ tagName: el.tagName.toLowerCase(), ...tag });
    });

    // Собираем скрипты
    doc.querySelectorAll("script").forEach((el) => {
      const script = {
        src: el.getAttribute("src") || null,
        content: el.innerHTML || null,
        async: el.hasAttribute("async"),
        defer: el.hasAttribute("defer"),
        type: el.getAttribute("type") || "text/javascript"
      };
      scripts.push(script);
    });

    return { tags, scripts };
  };

  const { tags } = useMemo(() => parseHtmlHead(htmlHead), [htmlHead]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: lang }}>
        <title>{title}</title>

        <link rel="icon" href={favicon} type="image/png" />
        <link rel="apple-touch-icon" href={appleTouchIcon} />

        {tags.map((tag, index) => {
          if (tag.tagName === "meta") {
            const { ...props } = tag;
            delete props.tagName;
            return <meta key={`meta-${index}`} {...props} />;
          }
          if (tag.tagName === "link") {
            const { ...props } = tag;
            delete props.tagName;
            return <link key={`link-${index}`} {...props} />;
          }
          return null;
        })}

        {/* {scripts?.map((script, indx) => {
          const props = { ...script };
          delete props.content;

          if (script.src) return <script key={indx} {...props} />;

          return (
            <script
              key={indx}
              {...props}
              content="console.log('f')"
              dangerouslySetInnerHTML={{ __html: script.content }}
            />
          );
        })} */}
      </Helmet>

      {htmlHead && <DynamicHead content={htmlHead} />}
      {htmlEndBody && <DynamicBody content={htmlEndBody} />}
    </>
  );
}
